import * as THREE from 'three'
import { FrameRenderer, SectionFrame, framesFromSections } from '@/shared/sectionFrames'
import attachFrameGizmo from './attachFrameGizmo'

/**
 * Draw a profile's sections with the frame projector, for V2.
 *
 * The cylindrical equivalent — mergeSections → createTargetObjectCanvas →
 * applyCanvasOverlayWithShader — unrolls each mesh onto one canvas and places sections on
 * it by angle. None of that happens here: a frame carries where it sits and which way it
 * faces, the projector works it out per fragment, and each section gets its own tile at
 * its own resolution. This is the same FrameRenderer the V3 editor uses and the same one
 * the Customizer will use, so what an operator authors is what a shopper sees.
 *
 * Reached only when the view profile resolves to `section_model: 'frame'`; see the guard
 * at the top of updateAllTargetObjects. Everything cylindrical keeps its existing path
 * untouched.
 */
export default function updateFrameSections(sections: any[], selectedSectionId: string | null = null): void {
  const viewer = (window as any).threeViewer
  const product: THREE.Object3D | undefined = viewer?.product
  if (!product) return

  // One renderer per loaded product. It owns per-mesh atlases and the cloned projector
  // materials, so rebuilding it on every edit would re-upload every texture; rebuilding it
  // when the product CHANGES is required, or it would keep painting a model that is gone.
  let renderer: FrameRenderer | undefined = viewer.frameRenderer
  if (!renderer || viewer.frameRendererRoot !== product) {
    renderer?.detach()
    renderer = new FrameRenderer()
    renderer.attach(product)
    viewer.frameRenderer = renderer
    viewer.frameRendererRoot = product
  }

  const frames = framesFromSections(sections ?? [])
  renderer.sync(frames, selectedSectionId)
  viewer.frameList = frames

  // Keep the drag gizmo on whichever section is selected. The handle is created lazily and
  // reused, so this is a no-op once attached.
  const gizmo = attachFrameGizmo({
    onChange: (frame) => {
      repaintFrame(frame)
      ;(window as any).threeViewer?.applyFrameEdit?.(frame, false)
    },
    onCommit: (frame) => {
      repaintFrame(frame)
      ;(window as any).threeViewer?.applyFrameEdit?.(frame, true)
    },
  })
  gizmo?.show(frames.find((f) => f.id === selectedSectionId) ?? null)
}

/**
 * Repaint one edited frame straight away, without going through React.
 *
 * A drag produces pointer moves faster than the form round-trip can answer, and that trip
 * is debounced on the way back — so if the painted section only followed the form, it
 * would lag behind the outline for the whole drag and settle afterwards. The form is still
 * told (that is what reaches the save payload); it just is not what drives the picture.
 *
 * The renderer is read off the viewer rather than captured, because the gizmo's handlers
 * are attached once and outlive any particular product load — a captured renderer would
 * carry on painting a model that has since been replaced.
 */
function repaintFrame(frame: SectionFrame): void {
  const viewer = (window as any).threeViewer
  const renderer: FrameRenderer | undefined = viewer?.frameRenderer
  if (!renderer) return

  const list: SectionFrame[] = (viewer.frameList ?? []).map((f: SectionFrame) => (f.id === frame.id ? frame : f))
  viewer.frameList = list
  renderer.sync(list, frame.id)
}

/**
 * Hand every mesh back its own material and forget the atlases.
 *
 * Called when a profile is torn down or switched, because the projector materials are
 * clones living on the meshes — dropping the renderer without this leaves the overlay
 * painted on a product nothing is managing any more.
 */
export function clearFrameSections(): void {
  const viewer = (window as any).threeViewer
  const renderer: FrameRenderer | undefined = viewer?.frameRenderer
  if (!renderer) return
  renderer.detach()
  viewer.frameRenderer = undefined
  viewer.frameRendererRoot = undefined
  viewer.frameList = undefined
  // The gizmo is parented to the scene, not the product, so it survives a teardown that
  // removes everything it was pointing at.
  viewer.__frameGizmoHandle?.detach()
}
